import React, { useEffect, useRef, useState } from 'react';
import { useLazyQuery, useMutation } from '@apollo/client';
import { isValidFiSerialNumber, isValidThirdPartySerialNumber } from '@barkinglabs/nano-shared';
import { ExternalChip } from '../../lib/types';
import { mapLookupChipToExternalChip } from '../../lib/chipAdapters';
import {
  externalChipLookupQuery,
  ExternalChipLookupData,
  externalChipForUnverifiedResellerLookupQuery,
  ExternalStrangerChipLookupData,
} from '../../lib/rescueQueries';
import { registerThirdPartyChip } from '@/appview/ResellerChipDetails/ResellerChipDetails.graphql';
import Button from '../../components/Button/Button';
import { useToast } from '../../components/Toast/ToastContext';
import { AAHASearchUrl, Links } from '@/lib/constants';
import { trackPetLookupSearched } from '../../analytics';
import styles from './Chips.module.scss';
import { StrangerChip } from '@/types/gql-op-types';
import { SUPPORT_EMAIL } from '@/rescue/lib/emails';

interface ExternalChipLookupProps {
  searchQuery: string;
  onViewExternal: (chip: ExternalChip) => void;
  onAddedToStock: () => void;
  onMatchChange: (hasMatch: boolean) => void;
  isVerifiedReseller: boolean;
}

function looksLikeValidSerial(value: string): boolean {
  const trimmed = value.trim();
  return isValidFiSerialNumber(trimmed) || isValidThirdPartySerialNumber(trimmed);
}

export default function ExternalChipLookup({
  searchQuery,
  onViewExternal,
  onAddedToStock,
  onMatchChange,
  isVerifiedReseller,
}: ExternalChipLookupProps) {
  const [searched, setSearched] = useState(false);
  const [searching, setSearching] = useState(false);
  const [result, setResult] = useState<ExternalChip | null>(null);
  const [resultForUnverifiedReseller, setResultForUnverifiedReseller] = useState<StrangerChip | null>(null);
  const [addingToStock, setAddingToStock] = useState(false);
  const { showToast } = useToast();

  const [fetchExternalChip] = useLazyQuery<ExternalChipLookupData>(externalChipLookupQuery);
  const [fetchExternalChipForUnverifiedReseller] = useLazyQuery<ExternalStrangerChipLookupData>(
    externalChipForUnverifiedResellerLookupQuery,
  );
  const [registerChip] = useMutation(registerThirdPartyChip);

  // Monotonic id for the most recent in-flight search; bumped on every new
  // query and on every runSearch start. The async runSearch checks this before
  // applying its result so a slow response from a stale query can't overwrite
  // newer state.
  const runIdRef = useRef(0);

  // Reset whenever the user types a new query, and invalidate any in-flight search.
  // onMatchChange is `setHasExternalMatch` from the parent — a stable useState setter,
  // so it stays out of the dep array.
  useEffect(() => {
    setSearched(false);
    setSearching(false);
    setResult(null);
    setResultForUnverifiedReseller(null);
    setAddingToStock(false);
    runIdRef.current++;
    onMatchChange(false);
  }, [searchQuery]);

  const isValidSerial = looksLikeValidSerial(searchQuery);

  async function runSearch() {
    const myId = ++runIdRef.current;
    setSearching(true);

    if (!isVerifiedReseller) {
      try {
        const { data } = await fetchExternalChipForUnverifiedReseller({ variables: { id: searchQuery } });
        if (myId !== runIdRef.current) {
          return;
        }
        if (data && data.chip) {
          setResultForUnverifiedReseller(data.chip);
          onMatchChange(true);
          trackPetLookupSearched(true);
        }
      } catch {
        if (myId !== runIdRef.current) {
          return;
        }
        setResult(null);
        setResultForUnverifiedReseller(null);
        onMatchChange(false);
        trackPetLookupSearched(false);
      } finally {
        if (myId === runIdRef.current) {
          setSearched(true);
          setSearching(false);
        }
      }
      return;
    }

    try {
      const { data, error } = await fetchExternalChip({ variables: { id: searchQuery } });
      if (myId !== runIdRef.current) {
        return;
      }
      if (error || !data?.resellerChip) {
        setResult(null);
        onMatchChange(false);
        trackPetLookupSearched(false);
      } else {
        setResult(mapLookupChipToExternalChip(data.resellerChip));
        onMatchChange(true);
        trackPetLookupSearched(true);
      }
    } catch {
      if (myId !== runIdRef.current) {
        return;
      }
      setResult(null);
      onMatchChange(false);
      trackPetLookupSearched(false);
    } finally {
      if (myId === runIdRef.current) {
        setSearched(true);
        setSearching(false);
      }
    }
  }

  // Auto-trigger the external lookup as soon as the empty state shows up for a
  // valid serial — no UI delay, no spinner, no CTA. The "No match found in your
  // stock" heading stays put while the request is in flight, then swaps to
  // either the result table or "No match found in your stock or the Fi Nano registry".
  const runSearchRef = useRef(runSearch);
  runSearchRef.current = runSearch;
  useEffect(() => {
    if (!isValidSerial || searched || searching) {
      return;
    }
    void runSearchRef.current();
  }, [isValidSerial, searched, searching, searchQuery]);

  async function handleAddToStock() {
    setAddingToStock(true);
    try {
      await registerChip({ variables: { input: { serialNumber: searchQuery } } });
      onAddedToStock();
    } catch (err) {
      showToast(`Failed to add chip to stock: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setAddingToStock(false);
    }
  }

  if (searched && result) {
    return (
      <div className={styles.lookupEngaged} data-hide-table-header="">
        <div className={styles.lookupResultHeader} data-overlay-table-header="">
          <span className={styles.lookupResultDot} aria-hidden="true" />
          <h3 className={styles.lookupHeading}>Match found in the Fi Nano registry</h3>
        </div>
        <div className={styles.lookupResults}>
          <table className={styles.lookupTable}>
            <thead>
              <tr>
                <th>Chip ID</th>
                <th>Pet</th>
                <th>Owner</th>
                <th>Organization</th>
                <th aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              <tr className={styles.lookupResultRow}>
                <td>
                  <div className={styles.lookupCellPrimary}>{result.id}</div>
                  <div className={styles.lookupSubtext}>{result.fullChipId}</div>
                </td>
                <td>
                  <div className={styles.lookupCellPrimary}>{result.petName || '—'}</div>
                  {result.breed && <div className={styles.lookupSubtext}>{result.breed}</div>}
                </td>
                <td>{result.ownerName || <span className={styles.lookupSubtext}>No owner</span>}</td>
                <td>{result.organization || 'External'}</td>
                <td className={styles.lookupActionCell}>
                  <Button variant="ghost" size="sm" onClick={() => onViewExternal(result)}>
                    View
                  </Button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    );
  }
  if (resultForUnverifiedReseller) {
    return (
      <div className={styles.lookupNoResultState} data-hide-table-header="" data-flush-thead="">
        <h3 className={styles.lookupHeading}>
          1 match found in Fi Nano registry for:
          <br />
          <br />
          <span style={{ fontSize: '120%' }}>{searchQuery.trim()}</span>
        </h3>
        <div className={styles.lookupAddToStockBlock}>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => window.open(Links.search(searchQuery.trim()), '_blank', 'noopener,noreferrer')}
          >
            View Chip
          </Button>
          <p className={styles.lookupSubtext}>
            Note: To reassign this chip to your organization, you must be verified.
            <br />
            Please{' '}
            <a href={`mailto:${SUPPORT_EMAIL}?subject=Chip%20Reseller%20Verification%20Request`}>contact support</a>
          </p>
        </div>
      </div>
    );
  }
  return (
    <div className={styles.lookupNoResultState} data-hide-table-header="" data-flush-thead="">
      <h3 className={styles.lookupHeading}>
        No match found in your stock or the Fi Nano registry for:
        <br />
        <br />
        <span style={{ fontSize: '120%' }}>{searchQuery.trim()}</span>
      </h3>

      {searched ? (
        <>
          <div className={styles.lookupAddToStockBlock}>
            <Button variant="green" onClick={handleAddToStock} disabled={addingToStock}>
              {addingToStock ? 'Adding…' : 'Add to Stock'}
            </Button>
            <p className={styles.lookupConfirmByline}>
              By clicking Add to Stock, I confirm I have required authority to claim this chip.
            </p>
          </div>
          <a
            href={AAHASearchUrl(searchQuery.trim())}
            target="_blank"
            rel="noopener noreferrer"
            className={styles.aahaLink}
          >
            Not a Fi Nano chip? Search AAHA Universal Microchip Lookup
          </a>
        </>
      ) : (
        <p className={styles.lookupSubtext}>
          For external chips, try searching with the full 15-digit chip ID or short code
        </p>
      )}
    </div>
  );
}
